/**
 * The one confirm for Complete and Bought as listed on a just-in-time PO
 * (BLU-1268), shared by the PO page and the To buy tab so the promise reads
 * the same in both places: it records what was bought and paid, and stock does
 * not change. The dialog stays open while the call runs and closes only when
 * `onConfirm` resolves true; a refusal has already raised its toast.
 */
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';

interface CompleteJustInTimeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Bought as listed (every line at its planned quantity) or a plain Complete. */
  asListed: boolean;
  poNumber: string;
  pending: boolean;
  onConfirm: () => Promise<boolean>;
}

export function CompleteJustInTimeDialog({
  open,
  onOpenChange,
  asListed,
  poNumber,
  pending,
  onConfirm,
}: CompleteJustInTimeDialogProps) {
  const { t } = useTranslation('orders');
  const handleConfirm = async () => {
    if (pending) return;
    const done = await onConfirm();
    if (done) onOpenChange(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={(next) => (pending ? undefined : onOpenChange(next))}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>
            {asListed ? t('jit.completeDialog.asListedTitle', { po: poNumber }) : t('jit.completeDialog.title', { po: poNumber })}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {asListed ? t('jit.completeDialog.asListedBody') : t('jit.completeDialog.body')}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>{t('jit.dialog.back')}</AlertDialogCancel>
          <Button
            onClick={() => void handleConfirm()}
            disabled={pending}
            aria-busy={pending || undefined}
            className="gap-1.5 bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
          >
            {pending && <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />}
            {asListed ? t('jit.completeDialog.asListedConfirm') : t('jit.completeDialog.confirm')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
